import { useState, useEffect } from "react";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEAKS = [
  { n: "01", t: "The Permission Problem", d: "You have a rule about when pleasure is allowed. You didn't choose it and you probably can't name it — and it's costing you more than you'd guess." },
  { n: "02", t: "The Rest That Doesn't Restore", d: "You're stopping. You're just not recovering. There's a real difference between the two, and hardly anyone gets taught it." },
  { n: "03", t: "The Beauty You Filter Out", d: "Your body registers beauty whether or not you're paying attention. Most of us are surrounded by far more than we ever take in." },
  { n: "04", t: "The Choice of Being the Strong One", d: "The leak isn't what you carry for everyone else. It's something quieter you hold back — and it costs more than the carrying does." },
  { n: "05", t: "The Wanting You Ignored", d: "Somewhere in the last decade you stopped asking what you want. What took its place looks like maturity. It in reality…isn't." },
];

const TAKEAWAYS = [
  "Which leak is actually yours — not a theory, your specific one",
  "Notice the mistakes that keep you drained",
  "Why these small things change your biology and not just your mood",
  "Identify playful and simple ways to implement the abstract theory",
  "Feel empowered to tackle your energy drains with practical tools",
  "An hour with women who recognize exactly what you're describing",
];

const WORKSHOP_FAQS = [
  { q: "Is it really free?", a: "Yes — the hour itself is free, and you'll leave with something useful whether or not we ever work together. We'll share how to go deeper with us at the end, for anyone who wants it." },
  { q: "Will there be a recording?", a: "No, and that's on purpose. What happens in this hour is honest and fairly intimate, and we'd rather keep it that way — people speak differently when nothing is being recorded. The shift doesn't come from knowing more anyway. It comes from the conversation. Everyone who registers gets a written recap of what we covered, and we run this weekly if the date doesn't suit." },
  { q: "How long is it?", a: "About an hour, with time for your questions." },
];

function DateLine({ info, className = "" }) {
  const sessions = info?.sessions?.length ? info.sessions : (info?.label ? [{ id: "s0", label: info.label, sub: info.sub }] : []);
  if (!sessions.length) {
    return (
      <p className={`font-mono-a text-[11px] sm:text-xs uppercase tracking-[0.18em] ${className}`}>
        This week · On Zoom · Free
      </p>
    );
  }
  return (
    <div className={className}>
      <p className="font-mono-a text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.2em]">
        This week · Two time zones · On Zoom · Free
      </p>
      <div className="mt-4 flex flex-col sm:flex-row items-stretch justify-center gap-3 sm:gap-4 max-w-md mx-auto">
        {sessions.map((s) => {
          const [datePart, ...rest] = s.label.split("·");
          const timePart = rest.join("·").trim();
          return (
            <div key={s.id} className="flex-1 rounded-md border-2 border-current/60 bg-current/[0.06] px-4 py-4 text-center">
              <span className="block font-mono-a text-xs sm:text-sm uppercase tracking-[0.14em] whitespace-nowrap">{datePart.trim()}</span>
              <span className="block font-serif text-2xl sm:text-3xl tracking-tight leading-none mt-2 normal-case">{timePart}</span>
              {s.sub && <span className="block font-mono-a text-[10px] uppercase tracking-[0.1em] opacity-85 mt-2.5">{s.sub}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PullQuote({ children }) {
  return (
    <Reveal>
      <blockquote className="my-12 text-center">
        <span className="block w-8 h-px bg-[#B85C38]/40 mx-auto mb-6" />
        <span className="font-serif italic text-xl sm:text-2xl leading-snug text-[#2C2A28]">{children}</span>
        <span className="block w-8 h-px bg-[#B85C38]/40 mx-auto mt-6" />
      </blockquote>
    </Reveal>
  );
}

function MidCTA({ onClick, label, variant = "solid" }) {
  const base = "rounded-full px-9 py-3.5 font-mono-a text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2";
  const styles =
    variant === "onDark"
      ? "bg-[#E7C6A5] text-[#2C2A28] hover:bg-[#F7F5F0]"
      : variant === "onColor"
      ? "bg-[#F7F5F0] text-[#B85C38] hover:bg-[#2C2A28] hover:text-[#F7F5F0]"
      : "bg-[#B85C38] text-[#F7F5F0] hover:bg-[#2C2A28]";
  return (
    <div className="mt-12 text-center">
      <button data-testid="workshop-mid-cta" onClick={onClick} className={`${base} ${styles}`}>
        {label} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function RegisterForm({ info }) {
  const featured = info?.featured?.length ? info.featured : (info?.sessions || []);
  const upcoming = info?.upcoming || [];
  const allSessions = [...featured, ...upcoming.flatMap((w) => w.sessions)];
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [sessionId, setSessionId] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  useEffect(() => {
    if (!sessionId && featured.length) {
      const firstOpen = featured.find((s) => !s.past) || featured[0];
      setSessionId(firstOpen.id);
    }
  }, [featured, sessionId]);

  const selected = allSessions.find((s) => s.id === sessionId);
  const selectedNotFeatured = selected && !featured.some((f) => f.id === selected.id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) { toast.error("Please add your first name."); return; }
    if (!form.email.includes("@")) { toast.error("Please add a valid email."); return; }
    if (allSessions.length && !sessionId) { toast.error("Please choose a time that suits you."); return; }
    if (!consent) { toast.error("Please tick the box so we can send you your Zoom details and recap."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/workshop/register`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, session_id: sessionId, consent, origin: window.location.origin }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch (err) {
      toast.error("Something didn't go through — please try again.");
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="workshop-success" className="text-center py-4">
        <span className="inline-grid place-items-center w-12 h-12 rounded-full bg-[#5C6B50]/10">
          <Check className="w-6 h-6 text-[#5C6B50]" />
        </span>
        <h3 className="font-serif text-2xl tracking-tight mt-4">You're in.</h3>
        <p className="text-[#595550] mt-3 leading-relaxed text-sm max-w-md mx-auto">
          Your Zoom details are on their way to your inbox — peek into your spam folder if you don't see them.
        </p>
        <p className="text-[#595550] mt-2 leading-relaxed text-sm max-w-md mx-auto">
          Please add <span className="text-[#B85C38]">hello@agelesswomanretreat.com</span> to your contacts so you don't miss the reminders and the written recap.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-md mx-auto">
      <input data-testid="workshop-first-name" type="text" value={form.first_name}
        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
        placeholder="First name"
        className="w-full bg-white/70 border hairline rounded-sm px-4 py-3.5 outline-none focus:border-[#B85C38] text-[#2C2A28]" />
      <input data-testid="workshop-email" type="email" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder="you@email.com"
        className="mt-3 w-full bg-white/70 border hairline rounded-sm px-4 py-3.5 outline-none focus:border-[#B85C38] text-[#2C2A28]" />

      {featured.length > 0 && (
        <div className="mt-4 text-left" data-testid="workshop-session-picker">
          <p className="font-mono-a text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A0472A] mb-2">Choose your time · this week</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {featured.map((s) => {
              const [datePart, ...rest] = s.label.split("·");
              const timePart = rest.join("·").trim();
              const active = sessionId === s.id;
              return (
                <label key={s.id}
                  data-testid={`workshop-session-${s.id}`}
                  className={`relative block cursor-pointer rounded-md border-2 p-4 transition-all ${active ? "border-[#B85C38] bg-[#B85C38]/[0.06] shadow-[0_8px_24px_-14px_rgba(184,92,56,0.7)]" : "border-[#E4DFD3] bg-white/60 hover:border-[#B85C38]/50"} ${s.past ? "opacity-45 pointer-events-none" : ""}`}>
                  <input type="radio" name="workshop-session" value={s.id}
                    checked={active} disabled={s.past}
                    onChange={() => setSessionId(s.id)}
                    className="sr-only" />
                  <span className={`flex items-center justify-between font-mono-a text-[10px] font-semibold uppercase tracking-[0.14em] ${active ? "text-[#A0472A]" : "text-[#6E665C]"}`}>
                    <span>{datePart.trim()}{s.past ? " · past" : ""}</span>
                    <span className={`grid place-items-center w-4 h-4 rounded-full border ${active ? "border-[#B85C38] bg-[#B85C38]" : "border-[#c9c1b3]"}`}>
                      {active && <Check className="w-2.5 h-2.5 text-[#F7F5F0]" strokeWidth={4} />}
                    </span>
                  </span>
                  <span className="block mt-2 font-serif text-2xl leading-none tracking-tight text-[#2C2A28]">{timePart}</span>
                  {s.sub && <span className="block mt-2 text-[11px] text-[#6E665C] leading-snug">{s.sub}</span>}
                </label>
              );
            })}
          </div>
        </div>
      )}

      <label className="mt-4 flex items-start gap-3 cursor-pointer text-left">
        <input data-testid="workshop-consent" type="checkbox" checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 accent-[#B85C38] cursor-pointer" />
        <span className="text-xs leading-relaxed text-[#595550]">
          Yes — send me my Zoom details, reminders and recap, and keep me posted with occasional notes, offers and event news from Monica Pandele &amp; Natalie Alcantara. I can unsubscribe anytime.
        </span>
      </label>
      <button data-testid="workshop-submit" type="submit" disabled={loading}
        className="mt-4 w-full bg-[#B85C38] text-[#F7F5F0] rounded-full py-4 font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {loading ? "Saving your place…" : <>Bring my energy back <ArrowRight className="w-4 h-4" /></>}
      </button>

      {selectedNotFeatured && (
        <div data-testid="workshop-selected-note" className="mt-3 flex items-center justify-between gap-3 rounded-sm bg-[#5C6B50]/10 border border-[#5C6B50]/25 px-3.5 py-2.5 text-left">
          <span className="text-xs leading-snug text-[#4a4642]">Registering for <strong className="text-[#2C2A28]">{selected.label}</strong></span>
          <button type="button" onClick={() => setSessionId(featured[0]?.id)}
            className="shrink-0 font-mono-a text-[9px] uppercase tracking-[0.16em] text-[#5C6B50] underline underline-offset-2 hover:text-[#2C2A28]">
            This week
          </button>
        </div>
      )}

      {upcoming.length > 0 && (
        <div className="mt-6">
          <button type="button" data-testid="workshop-see-upcoming" onClick={() => setShowUpcoming((v) => !v)}
            className="w-full flex items-center justify-center gap-2 rounded-full border-2 border-[#B85C38]/50 text-[#B85C38] py-3 font-mono-a text-[11px] uppercase tracking-[0.18em] hover:bg-[#B85C38]/[0.06] transition-colors">
            {showUpcoming ? "Hide other dates" : "Can't make it this week? See other dates"}
          </button>
          {showUpcoming && (
            <div data-testid="workshop-upcoming-list" className="mt-5 text-left space-y-6">
              {upcoming.map((wk, i) => (
                <div key={i}>
                  <p className="font-mono-a text-[11px] font-semibold uppercase tracking-[0.18em] text-[#A0472A] pb-2 border-b border-[#B85C38]/30">{wk.week_label}</p>
                  <div className="mt-2 space-y-2">
                    {wk.sessions.map((s) => {
                      const [datePart, ...rest] = s.label.split("·");
                      const timePart = rest.join("·").trim();
                      const active = sessionId === s.id;
                      return (
                        <button key={s.id} type="button" data-testid={`workshop-upcoming-${s.id}`}
                          onClick={() => { setSessionId(s.id); setShowUpcoming(false); }}
                          className={`w-full flex items-center justify-between gap-3 rounded-md border px-4 py-3 text-left transition-colors ${active ? "border-[#B85C38] bg-[#B85C38]/[0.07]" : "border-[#E4DFD3] bg-white/70 hover:border-[#B85C38]/60"}`}>
                          <span className="flex flex-col">
                            <span className="text-[15px] font-medium text-[#2C2A28]">{datePart.trim()}</span>
                            <span className="text-[13px] text-[#595550] mt-0.5">{timePart}{s.sub ? ` · ${s.sub}` : ""}</span>
                          </span>
                          <span className={`shrink-0 font-mono-a text-[10px] uppercase tracking-[0.16em] ${active ? "text-[#B85C38]" : "text-[#B85C38]"}`}>{active ? "✓ Selected" : "Choose →"}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <p className="mt-4 text-center text-xs text-[#9a938a]">Live only, no recording. Everyone who registers gets a written recap afterward.</p>
    </form>
  );
}

export default function Workshop() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    fetch(`${API}/workshop/info`).then((r) => r.json()).then(setInfo).catch(() => {});
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = "The Five Energy Leaks — Free Live Workshop with Monica & Natalie";
    if (meta) meta.setAttribute("content", "A free live hour on where your energy is quietly going — and the simplest things that bring it back. Live on Zoom, no recording, with a written recap for everyone who registers.");
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const toRegister = () => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-[#F7F5F0] text-[#2C2A28] min-h-screen">
      {/* 1 · HERO */}
      <section className="px-6 pt-16 pb-16 sm:pt-20 sm:pb-24 text-center max-w-3xl mx-auto">
        <img src="/images/logo-mark.png" alt="The Ageless Woman" className="h-12 w-12 mx-auto object-contain" />

        <figure className="mt-8 mx-auto w-full max-w-md">
          <img src="/images/workshop-hosts.jpg" alt="Monica and Natalie"
            className="w-full aspect-[4/5] object-cover object-top rounded-sm shadow-[0_20px_50px_-20px_rgba(44,42,40,0.5)]" />
        </figure>

        <p className="mt-9 font-mono-a text-[11px] sm:text-xs uppercase tracking-[0.25em] text-[#B85C38]">A free live hour with Monica &amp; Natalie</p>
        <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">The Five Energy Leaks</h1>
        <p className="mt-5 text-base sm:text-lg text-[#595550] leading-relaxed max-w-xl mx-auto">
          Where your energy is quietly going — and what actually brings it back.
        </p>
        <div className="mt-7">
          <DateLine info={info} className="text-[#5C6B50]" />
        </div>
        <button data-testid="workshop-hero-cta" onClick={toRegister}
          className="mt-8 bg-[#B85C38] text-[#F7F5F0] rounded-full px-10 py-4 font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] transition-colors inline-flex items-center gap-2">
          Bring my energy back <ArrowRight className="w-4 h-4" />
        </button>

        <div className="mt-9 max-w-md mx-auto">
          <p className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38]">Who this is for</p>
          <p className="mt-2 font-serif text-lg sm:text-xl leading-snug text-[#2C2A28]">
            Women who want more energy — and the vitality they crave, through the simplest things.
          </p>
        </div>

        <p className="mt-6 text-xs text-[#9a938a]">Live only, no recording. Everyone who registers gets a written recap afterward.</p>
      </section>

      {/* 2 · THE FIVE ENERGY LEAKS */}
      <section className="py-16 sm:py-24 px-6 max-w-2xl mx-auto">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38]">In the hour</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">The five energy leaks you ignored</h2>
          <p className="mt-3 text-[#595550] leading-relaxed text-[15px] sm:text-base">These are starving you from savoring your five dolce vita ingredients.</p>
        </Reveal>
        <Reveal>
          <div className="mt-6 space-y-5 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">
            <p>Your energy is your most important asset. You're likely already doing the hard work to keep it going — the right nutrition, the exercise, the supplements — and each of those helps in its own way.</p>
            <p>In this hour we go somewhere else: the traps that quietly keep you empty, and the wisdom that's been around for millennia on how to recharge from within.</p>
          </div>
        </Reveal>
        <div className="mt-12 space-y-5">
          {LEAKS.map((l) => (
            <Reveal key={l.n}>
              <div data-testid={`leak-${l.n}`} className="group relative flex gap-5 sm:gap-7 items-start rounded-sm bg-[#5C6B50]/[0.06] border-l-2 border-[#5C6B50] pl-5 sm:pl-7 pr-5 py-6 sm:py-7 transition-colors duration-300 hover:bg-[#5C6B50]/[0.11]">
                <span className="font-serif text-5xl sm:text-6xl text-[#5C6B50]/25 leading-none -mt-1 select-none">{l.n}</span>
                <div>
                  <h3 className="font-serif font-medium text-2xl sm:text-[1.9rem] leading-[1.1] tracking-tight text-[#5C6B50]">{l.t}</h3>
                  <p className="mt-3 text-[#595550] leading-relaxed text-[15px]">{l.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="mt-10 font-serif italic text-xl text-center text-[#2C2A28]">Which one is yours? That's what the hour is for.</p>
        </Reveal>
        <MidCTA onClick={toRegister} label="Find out which one is yours" variant="solid" />
      </section>

      {/* THE OBJECTION */}
      <section className="px-6 pb-16 max-w-2xl mx-auto">
        <Reveal>
          <div className="border hairline rounded-sm bg-[#EAE5D9]/40 px-6 py-8 sm:px-10 sm:py-10">
            <p className="text-[#595550] leading-[1.8] text-[15px] sm:text-base">
              You might be thinking: <span className="italic text-[#2C2A28]">I need things that are proven and actually work — not the fluff, not simple stuff I could figure out on my own.</span>
            </p>
            <p className="mt-4 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">
              The truth is, the simplest things are the hardest to spot — and harder still to actually live. That's exactly why we built an experience like this: to bring you back to basics, without overthinking or overcomplicating it.
            </p>
          </div>
        </Reveal>
      </section>

      {/* 3 · WHAT YOU'LL LEAVE WITH */}
      <section className="bg-[#2C2A28] text-[#F7F5F0] py-16 sm:py-24">
        <div className="px-6 max-w-2xl mx-auto">
          <Reveal>
            <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#E7C6A5]">By the end</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight">What you'll leave with</h2>
          </Reveal>
          <Reveal>
            <div className="mt-6 space-y-5 text-[#F7F5F0]/80 leading-[1.8] text-[15px] sm:text-base">
              <p>This isn't a theoretical workshop where we hand you a list of things to follow. You already have the answers — you just need to let them surface.</p>
              <p className="font-serif italic text-lg text-[#E7C6A5]">It's about activating your inner compass: the intuition that tells you your own truth.</p>
            </div>
          </Reveal>
          <ul className="mt-10 space-y-5">
            {TAKEAWAYS.map((t) => (
              <Reveal key={t}>
                <li className="flex items-start gap-3.5 text-[#F7F5F0]/85 leading-relaxed text-[15px] sm:text-base">
                  <span className="mt-1 w-5 h-5 rounded-full bg-[#E7C6A5]/15 grid place-items-center shrink-0">
                    <Check className="w-3 h-3 text-[#E7C6A5]" strokeWidth={3} />
                  </span>
                  {t}
                </li>
              </Reveal>
            ))}
          </ul>
          <MidCTA onClick={toRegister} label="Save my place — it's free" variant="onDark" />
        </div>
      </section>

      {/* YOUR HOSTS (guides) */}
      <section className="bg-[#EAE5D9]/60 py-16 sm:py-24">
        <div className="px-6 max-w-2xl mx-auto">
          <Reveal>
            <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38]">Your hosts</p>
          </Reveal>
          <div className="mt-10 space-y-12">
            <Reveal>
              <div className="grid sm:grid-cols-[minmax(0,200px)_1fr] gap-6 sm:gap-8 items-center">
                <img src="/images/monica.jpg" alt="Monica Pandele"
                  className="w-full aspect-[4/5] object-cover rounded-sm shadow-[0_16px_40px_-18px_rgba(44,42,40,0.5)]"
                  style={{ objectPosition: "72% 30%" }} />
                <div className="border-l-2 border-[#B85C38]/40 pl-5">
                  <h3 className="font-serif text-2xl tracking-tight">Monica Pandele</h3>
                  <p className="mt-1 font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#5C6B50]">Certified Energy Leadership Coach · xchange Guide · HeartMath Guide</p>
                  <p className="mt-3 text-[#595550] leading-relaxed text-[15px]">Twenty-five years spent studying how energy moves — through ecosystems, organisations, and human bodies. Including my own, the hard way.</p>
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div className="grid sm:grid-cols-[1fr_minmax(0,200px)] gap-6 sm:gap-8 items-center">
                <div className="border-l-2 border-[#B85C38]/40 pl-5 sm:order-1 order-2">
                  <h3 className="font-serif text-2xl tracking-tight">Natalie Alcantara</h3>
                  <p className="mt-1 font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#5C6B50]">Conversation Architect · xchange Guide · HeartMath Guide</p>
                  <p className="mt-3 text-[#595550] leading-relaxed text-[15px]">Using methodology trusted by leaders at BMW, NASA and Google. Italy adopted me years ago and has been teaching me about this ever since.</p>
                </div>
                <img src="/images/natalie.jpg" alt="Natalie Alcantara"
                  className="w-full aspect-[4/5] object-cover rounded-sm shadow-[0_16px_40px_-18px_rgba(44,42,40,0.5)] sm:order-2 order-1"
                  style={{ objectPosition: "35% 25%" }} />
              </div>
            </Reveal>
          </div>
          <Reveal>
            <blockquote className="mt-12 font-serif italic text-xl sm:text-2xl leading-relaxed text-[#2C2A28] text-center">
              "You cannot create from emptiness. You can perform from depletion — we're all experts at that — but it never lasts."
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* WHO THIS IS FOR */}
      <section className="py-16 sm:py-24 px-6 max-w-2xl mx-auto">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38]">Who this is for</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
            You're the one everyone leans on. <span className="italic">And lately, you've started to feel it.</span>
          </h2>
        </Reveal>
        <Reveal>
          <div className="mt-8 space-y-5 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">
            <p>Maybe something in your life has visibly shifted: your health, your relationship, your work, your family. Maybe nothing has, and that's almost stranger — everything looks fine and you still feel further from yourself than you'd like to admit.</p>
            <p>Maybe your body is changing in ways nobody prepared you for. Maybe the roles you built your identity on — the capable one, the one who holds it together, the mother, the professional, the strong friend — don't fit the way they used to, and you're quietly wondering who you are underneath them.</p>
            <p>Or maybe it's simpler and harder to name than any of that. Things are good. You're just not really in them.</p>
            <p className="font-serif italic text-lg text-[#2C2A28]">You don't have to be in crisis to come. You don't have to be depleted, or have a name for what's going on. You only have to be curious about what would actually bring you back.</p>
          </div>
        </Reveal>
      </section>

      {/* THE OPENING (our story) */}
      <section className="bg-[#EAE5D9]/60 py-16 sm:py-24">
        <div className="px-6 max-w-2xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight leading-tight">
              We're not teaching this from a book. <span className="italic">We've both lived it.</span>
            </h2>
          </Reveal>

          <Reveal>
            <div className="mt-8 space-y-5 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">
              <p>For Monica, it was a hum in the background for years — until perimenopause turned it into a spotlight and halved her aliveness. She kept going anyway, until the darkness made her stop and reset completely.</p>
              <p>For Natalie, it was the transitions and the losses. What carried her through wasn't strategy — it was Italy, teaching her to slow down enough to let life back in.</p>
            </div>
          </Reveal>

          <PullQuote>Different roads, same discovery. And it wasn't what either of us expected.</PullQuote>

          <Reveal>
            <div className="space-y-5 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">
              <p>It shows up differently for everyone. Something visible shifts — your health, your work, your body. Or nothing shifts at all, and you simply notice you're not quite in your own life anymore.</p>
              <p>Either way, you feel the depletion. You just can't say where it's all going. So you do what we did: push harder, get efficient, wait for it to pass.</p>
            </div>
          </Reveal>

          <PullQuote>The things that bring you back are the ones we called too small to matter.</PullQuote>

          <Reveal>
            <div className="space-y-5 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">
              <p>The long lunch. The good light. The walk with no purpose. We filed them under frivolous — something for women with fewer responsibilities than ours.</p>
              <p>They turned out to be the whole thing. These small things change your biology and your ability to reset. They're not the reward waiting on the other side of this — they're what carries you through it.</p>
            </div>
          </Reveal>

          <PullQuote>La dolce vita was never about leisure. It's about how life gets back in.</PullQuote>

          <Reveal>
            <div className="space-y-5 text-[#4a4642] leading-[1.8] text-[15px] sm:text-base">
              <p>Through your senses, through beauty, through the table, through whatever is in front of you.</p>
              <p className="text-[#B85C38]">The five leaks are the five places we quietly close that door.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7 · WHY WE'RE CREATING THIS */}
      <section className="bg-[#B85C38] text-[#F7F5F0] py-16 sm:py-24">
        <div className="px-6 max-w-2xl mx-auto">
          <Reveal>
            <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#F7F5F0]/70">Why we're creating this</p>
          </Reveal>
          <Reveal>
            <div className="mt-8 space-y-6 leading-[1.7]">
              <p className="font-serif text-2xl sm:text-3xl tracking-tight leading-snug">
                We stand for each woman living life to the fullest — creating from her full potential, and enjoying the magic that surrounds us in every moment.
              </p>
              <p className="text-[#F7F5F0]/85 text-[15px] sm:text-base leading-[1.8]">
                We believe in regenerative systems, where a woman embodies the vital matriarch — not power <em>over</em> anyone, but power <em>with</em>. The woman whose own aliveness holds and lifts everyone around her.
              </p>
              <p className="text-[#F7F5F0]/85 text-[15px] sm:text-base leading-[1.8]">
                She's stopped trying to control, to slow her age, or to mask her true essence — and started inhabiting her body as a source of strength.
              </p>
            </div>
          </Reveal>
          <MidCTA onClick={toRegister} label="Come be part of it" variant="onColor" />
        </div>
      </section>

      {/* 7b · A SPONTANEOUS MOMENT */}
      <section className="bg-[#F7F5F0] py-16 sm:py-20">
        <div className="px-6 max-w-2xl mx-auto text-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm shadow-[0_20px_50px_-20px_rgba(44,42,40,0.5)]">
              <video
                data-testid="workshop-moment-video"
                src="/images/hosts-moment.mp4"
                autoPlay muted loop playsInline
                className="w-full h-auto block"
              />
            </div>
            <p className="mt-4 font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#9a938a]">This is the energy. Nothing staged.</p>
          </Reveal>
        </div>
      </section>

      {/* 7 · QUESTIONS */}
      <section className="py-16 sm:py-24 px-6 max-w-2xl mx-auto">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38]">Questions</p>
        </Reveal>
        <div className="mt-8 space-y-8">
          {WORKSHOP_FAQS.map((f) => (
            <Reveal key={f.q}>
              <div className="border-b hairline pb-8">
                <h3 className="font-serif text-xl sm:text-2xl tracking-tight">{f.q}</h3>
                <p className="mt-3 text-[#595550] leading-relaxed text-[15px]">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 8 · FINAL BLOCK */}
      <section id="register" className="bg-[#5C6B50] py-16 sm:py-24">
        <div className="px-6 max-w-2xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F7F5F0] leading-tight">
              Be alive through the <span className="italic">simplest things</span> in life.
            </h2>
            <p className="mt-5 font-mono-a text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-[#F4E3CF]">
              This week · Two time zones · On Zoom · Free
            </p>
          </Reveal>
          <div className="mt-10 bg-[#F7F5F0] rounded-sm p-6 sm:p-10">
            <RegisterForm info={info} />
          </div>
          <p className="mt-6 text-sm text-[#F7F5F0]/70 leading-relaxed max-w-md mx-auto">
            Live only, no recording. We run it weekly — keep an eye on upcoming dates.
          </p>
        </div>
      </section>

      <footer className="bg-[#2C2A28] text-center py-8 px-6">
        <a href="/" className="font-serif text-lg text-[#F7F5F0]/80 hover:text-[#E7C6A5] transition-colors">The Ageless Woman</a>
        <p className="mt-1 font-mono-a text-[10px] uppercase tracking-[0.18em] text-[#F7F5F0]/40">agelesswomanretreat.com</p>
      </footer>
    </div>
  );
}
